import React, { FC, useCallback, useMemo } from 'react';
import InfiniteLoader from 'react-window-infinite-loader';
import { FixedSizeList } from 'react-window';

import { usePlayerContext } from 'application/providers/PlayerProvider';

import { DATA_TEST_IDS, PROMOS_LIST_HEIGHT } from '../../consts';
import PromoListSkeleton from '../PromoListSkeleton';
import PromoOption from '../PromoOption';
import type { PromoListItemData } from '../types';
import { PROMO_MOCK_PAGE_SIZE, type PromoMockTrack } from '../fixtures';

import styles from './index.module.scss';

const PROMO_LIST_ITEM_HEIGHT = 82;
const PROMO_LIST_PRELOAD_THRESHOLD = 4;
const PROMO_LIST_LOADING_ITEM_KEY = 'promo-loading-row';

type PromoOptionsListProps = {
  hasMoreItems: boolean;
  isLoadingMore: boolean;
  items: PromoMockTrack[];
  selectedPromoIdSet: Set<string>;
  onLoadMore: () => Promise<void>;
  onPromoPlaybackToggle: (promo: PromoMockTrack) => void;
  onPromoSelectionChange: (promoId: string, isSelected: boolean) => void;
};

const PromoOptionsList: FC<PromoOptionsListProps> = ({
  hasMoreItems,
  isLoadingMore,
  items,
  selectedPromoIdSet,
  onLoadMore,
  onPromoPlaybackToggle,
  onPromoSelectionChange,
}) => {
  const { fileUuid, isLoading, isPaused, pause, resume } = usePlayerContext();
  const listData = useMemo<PromoListItemData>(
    () => ({
      items,
      fileUuid,
      isLoading,
      isPaused,
      isItemSelected: (promoId) => selectedPromoIdSet.has(promoId),
      onPromoPlaybackToggle,
      onPromoSelectionChange,
      pause,
      resume,
    }),
    [
      fileUuid,
      isLoading,
      isPaused,
      items,
      onPromoPlaybackToggle,
      onPromoSelectionChange,
      pause,
      resume,
      selectedPromoIdSet,
    ]
  );
  const itemCount = items.length + Number(hasMoreItems);
  const isItemLoaded = useCallback(
    (index: number) => !hasMoreItems || index < items.length,
    [hasMoreItems, items.length]
  );
  const loadMoreItems = useCallback(() => onLoadMore(), [onLoadMore]);
  const getItemKey = useCallback(
    (index: number, data: PromoListItemData) => data.items[index]?.id ?? PROMO_LIST_LOADING_ITEM_KEY,
    []
  );

  return (
    <div
      className={styles.list}
      data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_LIST}>
      {items.length === 0 && isLoadingMore ? (
        <PromoListSkeleton />
      ) : (
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={itemCount}
          loadMoreItems={loadMoreItems}
          minimumBatchSize={PROMO_MOCK_PAGE_SIZE}
          threshold={PROMO_LIST_PRELOAD_THRESHOLD}>
          {({ onItemsRendered, ref }) => (
            <FixedSizeList
              ref={ref}
              className={styles.listInner}
              data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_VIRTUAL_LIST}
              height={PROMOS_LIST_HEIGHT}
              itemCount={itemCount}
              itemData={listData}
              itemKey={getItemKey}
              itemSize={PROMO_LIST_ITEM_HEIGHT}
              width="100%"
              onItemsRendered={onItemsRendered}>
              {PromoOption}
            </FixedSizeList>
          )}
        </InfiniteLoader>
      )}
    </div>
  );
};

export default PromoOptionsList;
